import React, { useEffect, useState } from 'react';
import {CSVLink} from 'react-csv';
import { useTranslation } from 'react-i18next';
import { useInstance } from 'react-ioc';
import { ExportOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Skeleton } from 'antd';
import cn from 'classnames';
import dayjs from 'dayjs';
import _ from 'lodash';
import { observer } from 'mobx-react-lite';

import { Store } from '../../model/store/Store';
import { IStatus } from '../../shared/status';
import { FullSecondsFormat } from '../../shared/TimeFormat';

import { BoardData } from './Board';
import { Card } from './Card';

import cls from './Board.module.scss';

export interface IColumn {
    loading?: boolean;
    status: IStatus;
    data: BoardData[];
    onCreate: (status: string) => void;
    onCardOpen: (id: string) => void;
    onCardDrag: (id: string) => void;
    onColumnDrop: (status: string) => void;
    small?: boolean;
}

export const Column = observer(
    ({ loading, status, data, onCreate, onCardOpen, onCardDrag, onColumnDrop, small }: IColumn) => {
        const [isOver, setIsOver] = useState(false);
        const [wipLimit, setWipLimit] = useState(false);

        const { t } = useTranslation();

        const { auth } = useInstance(Store);

        const [time, setTime] = useState(dayjs());
        useEffect(() => {
            const interval = setInterval(() => setTime(dayjs()), 1000);
            return () => {
                clearInterval(interval);
            };
        }, []);

        function handleDragOver(e: any) {
            if (e.dataTransfer.types[0] === 'text/plain') {
                setIsOver(true);
                if (status.focus) {
                    // если есть мои задачи...
                    if (data.length > 0) {
                        setWipLimit(true);
                    }
                }
                e.preventDefault();
            }
            // console.log('over', e);
        }

        function handleDrop(e: any) {
            const dataJSON = e.dataTransfer.getData('text/plain');
            let data;
            try {
                data = JSON.parse(dataJSON);
            } catch {
                //null
            }
            if (data && data.type === 'card') {
                setIsOver(false);
                setWipLimit(false);
                onColumnDrop(status.name);
            }
            // console.log('drop', e);
        }

        function handleDragLeave() {
            setIsOver(false);
            setWipLimit(false);
        }

        let calcDate = undefined;
        if (status.focus && auth.data?.focusedAt) {
            calcDate = dayjs.duration((time.unix() - dayjs(auth.data?.focusedAt).unix()) * 1000);
        }

        const csvData = _.map(data, item => (
            {
                key: item.id,
                name: item.name,
                client: item.client?.name,
                group: item.group?.name,
                team: JSON.stringify(_.map(item.users, user => user.name)).replace(/["[\]]/g, ''),
                hours: (Math.round(item.reportsMinutes / 60 * 100) / 100).toString().replace('.', ','),
            }
        ))

        const csvHeaders = [
            {
                label: 'ID',
                key: 'key',
            },
            {
                label: 'Название',
                key: 'name',
            },
            {
                label: 'Клиент',
                key: 'client',
            },
            {
                label: 'Группа',
                key: 'group',
            },
            {
                label: 'Команда',
                key: 'team',
            },
            {
                label: 'Часы',
                key: 'hours',
            },
        ]

        return (
            <div
                className={cn([cls.column, small && cls.small])}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onDragLeave={handleDragLeave}
            >
                <div className={cn([cls.dragArea, isOver && cls.dragged, wipLimit && cls.wipLimit])} />
                {status.focus && <div className={cn([cls.focus, small && cls.small])} />}

                {small ? (
                    <div
                        className={cls.marker}
                        style={{ color: status.color || '#FFFFFF', backgroundColor: status.bgColor || '#202020' }}
                    />
                ) : (
                    <div
                        className={cls.title}
                        style={{ color: status.color || '#FFFFFF', backgroundColor: status.bgColor || '#202020' }}
                    >
                        <span>{t(status.name)}</span>
                        <span>
                            {status.name === 'Completed' &&
                                <CSVLink data={csvData} headers={csvHeaders} filename={`Saturn Completed ${dayjs().format('DD.MM.YYYY')}.csv`} separator={';'}>
                                    <Button className={cls.button} type={'text'} size={'small'} shape={'circle'}><ExportOutlined/></Button>
                                </CSVLink>
                            }
                            {data.length}
                        </span>
                    </div>
                )}

                {loading ? (
                    <Skeleton.Input block active />
                ) : (
                    _.map(data, (card) => (
                        <Card
                            key={card.id}
                            small={small}
                            data={card}
                            onCardOpen={() => onCardOpen(card.id)}
                            onCardDrag={() => onCardDrag(card.id)}
                        />
                    ))
                )}

                {status.focus || (
                    <Button
                        shape={'round'}
                        type={'text'}
                        block
                        icon={<PlusOutlined />}
                        onClick={() => onCreate && onCreate(status.name)}
                    />
                )}

                {calcDate && <div className={cls.timer}>{FullSecondsFormat(calcDate.asSeconds())}</div>}
            </div>
        );
    },
);
